import { LEGAL } from "@/lib/legal";
import { LegalLayout, Section, Bullets } from "./LegalLayout";

/**
 * Terms of service.
 *
 * Not strictly required by Play for every app, but this one has accounts and
 * republishes third-party content, which are the two cases where a reviewer
 * expects terms and an explicit "not affiliated with YouTube" statement. The
 * affiliation section is the one that most often decides a Deceptive Behaviour
 * rejection for an aggregator, so keep it prominent.
 */
export const TermsPage = () => (
  <LegalLayout
    title="Terms of Service"
    subtitle={`The agreement between you and ${LEGAL.entity} for using ${LEGAL.appName}.`}
  >
    <p>
      By installing or using {LEGAL.appName} you agree to these terms. If you do
      not agree, do not use the app.
    </p>

    <Section heading="What TrendingZ is">
      <p>
        {LEGAL.appName} is a discovery app. It collects publicly available
        information about videos that are trending on third-party platforms and
        presents it as rankings, charts and lists. Videos play through the
        official embedded player of the source platform, or open on that
        platform directly.
      </p>
    </Section>

    <Section heading="Not affiliated with YouTube or Google">
      <p>
        {LEGAL.appName} is an independent product. It is not affiliated with,
        endorsed by, sponsored by or in any way officially connected to YouTube,
        Google LLC, or any other platform whose content it indexes. All product
        names, logos, trademarks and video content remain the property of their
        respective owners and are used for identification only. Your use of
        embedded video is also subject to the{" "}
        <a
          className="text-orange-400 hover:text-orange-300"
          href="https://www.youtube.com/t/terms"
          target="_blank"
          rel="noreferrer noopener"
        >
          YouTube Terms of Service
        </a>
        .
      </p>
    </Section>

    <Section heading="Accounts">
      <Bullets
        items={[
          "An account is optional. The app is fully usable without one.",
          "You must be at least 13 years old, or the minimum age of digital consent where you live, whichever is higher.",
          "You are responsible for keeping your password confidential and for activity under your account.",
          "Provide an email address you control. We may suspend accounts used for abuse, automated scraping or attempts to break the service.",
          "You can delete your account at any time from Account inside the app, or from the web at /delete-account.",
        ]}
      />
    </Section>

    <Section heading="Acceptable use">
      <p>You agree not to:</p>
      <Bullets
        items={[
          "Scrape, mirror, resell or redistribute the rankings or data in bulk.",
          "Interfere with, overload or attempt to gain unauthorised access to the service or its APIs.",
          "Use the app to infringe anyone's intellectual property or privacy rights.",
          "Use the app for anything unlawful, or in breach of the terms of the source platforms.",
        ]}
      />
    </Section>

    <Section heading="Third-party content">
      <p>
        We do not host, own or moderate the videos themselves. Titles,
        thumbnails, channel names and view counts come from the source platforms
        and may be inaccurate, out of date or removed at any time. Rankings are
        our own calculation and are provided for general interest, not as a
        statement of fact about any platform's official charts. Report content
        concerns to the platform hosting the video; you may also contact us at{" "}
        <a className="text-orange-400 hover:text-orange-300" href={`mailto:${LEGAL.supportEmail}`}>
          {LEGAL.supportEmail}
        </a>{" "}
        and we will remove a listing where appropriate.
      </p>
    </Section>

    <Section heading="Our content">
      <p>
        The app itself, its design, code and the rankings we produce belong to{" "}
        {LEGAL.entity}. You get a personal, non-exclusive, non-transferable,
        revocable licence to use the app for its intended purpose. Nothing here
        transfers ownership.
      </p>
    </Section>

    <Section heading="Availability and changes">
      <p>
        The service is provided on an "as is" and "as available" basis. Features
        may change, and access may be interrupted for maintenance or for reasons
        outside our control, including changes made by the source platforms. We
        may update these terms; continued use after an update means you accept
        the revised version.
      </p>
    </Section>

    <Section heading="Disclaimer and liability">
      <p>
        To the fullest extent permitted by law, we disclaim all warranties,
        express or implied, including merchantability, fitness for a particular
        purpose and non-infringement. We are not liable for indirect,
        incidental, special or consequential damages, or for loss of data or
        profits, arising from your use of the app. Nothing in these terms limits
        liability that cannot be limited under applicable law, including your
        statutory consumer rights.
      </p>
    </Section>

    <Section heading="Termination">
      <p>
        You may stop using the app and delete your account at any time. We may
        suspend or terminate access if these terms are breached. Sections that
        by their nature should survive termination will survive it.
      </p>
    </Section>

    <Section heading="Governing law">
      <p>
        These terms are governed by the laws of {LEGAL.jurisdiction}, without
        regard to conflict-of-law rules, and the courts of {LEGAL.jurisdiction}{" "}
        will have jurisdiction, except where mandatory local law gives you the
        right to bring proceedings where you live.
      </p>
    </Section>

    <Section heading="Contact">
      <p>
        {LEGAL.entity} &middot;{" "}
        <a className="text-orange-400 hover:text-orange-300" href={`mailto:${LEGAL.supportEmail}`}>
          {LEGAL.supportEmail}
        </a>
      </p>
    </Section>
  </LegalLayout>
);

export default TermsPage;
